// UsersEditContent.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, MenuProps, message, UploadFile } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  EllipsisOutlined,
  ExclamationCircleFilled,
  HomeOutlined,
} from "@ant-design/icons";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import UsersForm from "../Form";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import {
  useMembershipCreate,
  UserCreateParams,
  UserParams,
  UserUpdateParams,
  useUserDelete,
  useUserGetById,
  useUserUpdate,
} from "../../../../hooks/api/users";
import { useNavigate, useParams } from "react-router";
import { User, UserRoleType } from "../../../../store/auth";
import { generateFileObjFromUrl } from "../../../Common/ImageUpload";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import { Role } from "../../../../enums/user";
import { VaadMembership } from "../Form/VaadMembershipsSection";
import { useVaadTableGet } from "../../../../hooks/api/vaads";

const initialState: UserParams = {
  id: "",
  fullName: "",
  email: "",
  role: "student",
  plan: "",
  birthdate: null,
  gender: "",
  address: "",
  country: "",
  city: "",
  zipCode: "",
  phone: "",
};

const prepareValues = (data: UserParams): UserParams => ({
  ...initialState,
  ...data,
  birthdate: data.birthdate ? dayjs(data.birthdate) : null,
});

// ← БЭК → ФОРМА: маппинг данных с бэкенда в VaadMembership
const mapBackendToForm = (memberships: any[] | undefined): VaadMembership[] => {
  if (!memberships) return [];

  return memberships.map((m) => ({
    id: m.id,
    vaadId: m.vaadId || m.vaad?.id,
    vaadUuid: m.vaad?.id,
    vaadName: m.vaadName,
    vaadStatus: m.vaadStatus || m.vaad?.status,
    subscriptionStatus: m.subscriptionStatus,
    userStatus: m.userStatus,
    discountValue: m.discountValue || 0,
    discountEndDate: m.discountEndDate ? dayjs(m.discountEndDate) : null,
    numberOfMonths: m.numberOfMonths ?? null,
    stripeManageUrl: m.stripeManageUrl || "",
    subscriptionTill: m.subscriptionTill ? dayjs(m.subscriptionTill) : null,
    stripeSubscriptionId: m.stripeSubscriptionId ?? null,
    subscriptionCanceledAt: m.subscriptionCanceledAt
      ? dayjs(m.subscriptionCanceledAt)
      : null,
  }));
};

const mapFormToBackend = (
  formValues: UserUpdateParams,
  memberships: VaadMembership[],
  originalData: any,
): any => {
  const payload: Record<string, unknown> = {
    fullName: formValues.fullName,
    phone: formValues.phone,
    address: formValues.address,
    memberships: memberships.map((m) => {
      const membership: Record<string, unknown> = {
        id: m.id,
        userStatus: m.userStatus,
        // subscriptionStatus: m.subscriptionStatus || null,
        discountValue: m.discountValue,
        discountEndDate: m.discountEndDate
          ? m.discountEndDate.toISOString()
          : null,
        numberOfMonths: m.numberOfMonths ?? null,
      };

      // subscriptionTill is only editable (and thus sent) for memberships that
      // are not managed by Stripe (no stripeSubscriptionId).
      if (!m.stripeSubscriptionId) {
        membership.subscriptionTill = m.subscriptionTill
          ? m.subscriptionTill.toISOString()
          : null;
      }

      // Drop null/undefined fields so they aren't sent at all.
      return Object.fromEntries(
        Object.entries(membership).filter(
          ([, value]) => value !== null && value !== undefined,
        ),
      );
    }),
  };

  // Drop null/undefined top-level fields so they aren't sent at all.
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  );
};

export default function UsersEditContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState(initialState);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [memberships, setMemberships] = useState<VaadMembership[]>([]);
  const [availableVaads, setAvailableVaads] = useState<
    { id: string; name: string }[]
  >([]);
  const vaadsTableGet = useVaadTableGet();
  const vaadMapRef = useRef<Map<string, { name: string; id: string }>>(
    new Map(),
  );
  const { open, contextHolder } = useSimpleModal();
  const userById = useUserGetById(id);
  const userUpdate = useUserUpdate();
  const userDelete = useUserDelete(id);
  const membershipCreate = useMembershipCreate();

  useEffect(() => {
    const loadVaads = async () => {
      try {
        const res = await vaadsTableGet.fetch({ page: 1, pageSize: 100 });
        const data = res?.data || [];

        const vaadsList = data.map((item: any) => ({
          id: item.id,
          name: item.name,
        }));

        setAvailableVaads(vaadsList);

        data.forEach((item: any) => {
          vaadMapRef.current.set(item.id, {
            name: item.name,
            id: item.id,
          });
        });
      } catch (error) {
        console.error("Failed to load vaads:", error);
      }
    };

    loadVaads();
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.users"), href: "/users" },
      { title: t("common.edit") },
    ]);
  }, [i18n.language, t]);

  useEffect(() => {
    if (userById.data && !userById.loading && !userById.error) {
      const values = prepareValues(userById.data);
      setInitialValues(values);
      form.setFieldsValue(values);

      const backendMemberships = mapBackendToForm(userById.data.memberships);
      setMemberships(backendMemberships);

      if (values.avatar) {
        setFileList([generateFileObjFromUrl(values.avatar)]);
      }
    }
  }, [userById.data, userById.loading, userById.error]);

  useEffect(() => {
    form.setFieldValue("avatar", fileList[0]?.url || "");
  }, [fileList]);

  useEffect(() => {
    if (!userDelete.error && !userDelete.loading && userDelete.data) {
      navigate(`/users/`);
    }
  }, [userDelete.error, userDelete.loading, userDelete.data]);

  const items: MenuProps["items"] = [
    {
      key: "delete",
      type: "item",
      label: <span style={{ color: "red" }}>{t("common.delete")}</span>,
      onClick: () => {
        open({
          icon: <ExclamationCircleFilled />,
          title: t("pages.users.deleteUserTitle"),
          content: (
            <span>
              {t("pages.users.deleteUser")}{" "}
              <b>{userById.data?.fullName || "-"}</b>?
            </span>
          ),
          cancelText: t("common.cancel"),
          okText: t("common.delete"),
          okButtonProps: { danger: true },
          onOk: () => userDelete.fetch(),
        });
      },
    },
  ];

  // const handleSubmit = async (values: UserUpdateParams) => {
  //   form.validateFields().then(() => {
  //     const payload = mapFormToBackend(values, memberships, userById.data);
  //     userUpdate.fetch(payload, id);
  //   });
  // };
  const handleSubmit = async (values: UserUpdateParams) => {
    form.validateFields().then(async () => {
      const existingMemberships = memberships.filter(
        (m) => !m.id.startsWith("vaad-"),
      );
      const payload = mapFormToBackend(
        values,
        existingMemberships,
        userById.data,
      );
      await userUpdate.fetch(payload, id);

      const newMemberships = memberships.filter((m) =>
        m.id.startsWith("vaad-"),
      );

      for (const newMembership of newMemberships) {
        try {
          await membershipCreate.fetch({
            user: { id: id! },
            vaad: { id: newMembership.vaadUuid || newMembership.vaadId },
            subscriptionType: "monthly",
            discountValue: newMembership.discountValue,
            discountEndDate: newMembership.discountEndDate
              ? newMembership.discountEndDate.toISOString()
              : null,
            numberOfMonths: newMembership.numberOfMonths ?? undefined,
          });
        } catch (error) {
          message.error(
            `Failed to add membership for ${newMembership.vaadName}`,
          );
        }
      }

      // message.success("All changes saved successfully");
      navigate(-1);
    });
  };

  useMessageSuccess([userUpdate], "All changes saved successfully");
  useMessageError([userUpdate]);

  return (
    <SinglePageContainer
      cardLoading={userById.loading}
      title={t("pages.users.userEdit")}
      onBack={() => navigate(-1)}
      extra={
        <>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSubmit}
        >
          <UsersForm
            fileList={fileList}
            setFileList={setFileList}
            memberships={memberships}
            availableVaads={availableVaads}
            onMembershipsChange={setMemberships}
            onSubscriptionCanceled={() => userById.fetch(undefined, id)}
          />
        </Form>
      </div>
      {contextHolder}
    </SinglePageContainer>
  );
}
